import type { GameSettings } from '../app/settings.js';

type SfxKind =
  | 'connect'
  | 'deliver'
  | 'reward'
  | 'alert'
  | 'defeat'
  | 'draw'
  | 'spawnGen'
  | 'spawnBulb';

/**
 * Generative soft soundtrack: several phrases, rests, and occasional
 * harmony — discrete notes only (no continuous drones).
 */
export class AudioManager {
  private ctx: AudioContext | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private musicTimer: number | null = null;
  private step = 0;
  private phrase = 0;
  private lastSfx = new Map<SfxKind, number>();
  private settings: GameSettings;
  private started = false;

  constructor(settings: GameSettings) {
    this.settings = settings;
  }

  updateSettings(s: GameSettings): void {
    this.settings = s;
    if (this.musicGain) {
      this.musicGain.gain.value = s.musicVolume <= 0 ? 0 : Math.max(0, s.musicVolume) * 0.26;
    }
    if (this.sfxGain) this.sfxGain.gain.value = Math.max(0, s.sfxVolume);
    if (s.musicVolume <= 0) {
      this.stopMusic();
    } else if (this.started && this.musicTimer === null) {
      this.startMelody();
    }
  }

  ensureStarted(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      if (this.settings.musicVolume > 0 && this.musicTimer === null) this.startMelody();
      return;
    }
    const ctx = new AudioContext();
    this.ctx = ctx;
    this.musicGain = ctx.createGain();
    this.sfxGain = ctx.createGain();
    this.musicGain.connect(ctx.destination);
    this.sfxGain.connect(ctx.destination);
    this.started = true;
    this.updateSettings(this.settings);
    if (this.settings.musicVolume > 0) this.startMelody();
  }

  stopMusic(): void {
    if (this.musicTimer !== null) {
      window.clearInterval(this.musicTimer);
      this.musicTimer = null;
    }
    if (this.musicGain) this.musicGain.gain.value = 0;
  }

  private startMelody(): void {
    if (!this.ctx || !this.musicGain || this.settings.musicVolume <= 0) return;
    this.stopMusic();
    this.musicGain.gain.value = Math.max(0, this.settings.musicVolume) * 0.26;
    this.step = 0;
    this.phrase = 0;

    // Four contrasting phrases in A minor / C major flavours (Hz).
    const phrases: (number | null)[][] = [
      [220, 261.63, 329.63, null, 392, 329.63, 261.63, null],
      [196, 246.94, 293.66, 349.23, null, 293.66, 246.94, 220],
      [261.63, null, 329.63, 392, 440, null, 392, 329.63],
      [174.61, 220, null, 261.63, 329.63, 261.63, null, 196],
      [246.94, 293.66, 349.23, null, null, 293.66, 246.94, null],
      [220, 277.18, 329.63, 370, null, 329.63, 277.18, 220],
    ];

    this.musicTimer = window.setInterval(() => {
      if (!this.ctx || this.settings.musicVolume <= 0) {
        this.stopMusic();
        return;
      }
      const phraseNotes = phrases[this.phrase % phrases.length]!;
      const idx = this.step;
      const note = phraseNotes[idx]!;
      this.step++;

      if (this.step >= phraseNotes.length) {
        this.step = 0;
        this.phrase++;
        // Longer silence every 3 phrases
        if (this.phrase % 3 === 0) return;
      }

      if (note == null) return;

      const soft = this.phrase % 2 === 1;
      this.playMelodyNote(note, soft ? 0.05 : 0.07);
      if (this.phrase % 4 === 2 && idx % 2 === 0) {
        this.playMelodyNote(note * 1.5, 0.03);
      }
    }, 520);
  }

  private playMelodyNote(freq: number, peak = 0.07): void {
    if (!this.ctx || !this.musicGain || this.settings.musicVolume <= 0) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    const f = this.ctx.createBiquadFilter();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, t);
    f.type = 'lowpass';
    f.frequency.value = 1400;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(peak, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.7);
    osc.connect(f);
    f.connect(g);
    g.connect(this.musicGain);
    osc.start(t);
    osc.stop(t + 0.75);
  }

  private beep(freq: number, dur: number, type: OscillatorType = 'sine', vol = 0.15): void {
    if (!this.ctx || !this.sfxGain || this.settings.sfxVolume <= 0) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    const f = this.ctx.createBiquadFilter();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    // Soften highs — warmer, less piercing SFX.
    f.type = 'lowpass';
    f.frequency.value = 1200;
    f.Q.value = 0.7;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.015);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(f);
    f.connect(g);
    g.connect(this.sfxGain);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  playSfx(kind: SfxKind): void {
    this.ensureStarted();
    const now = performance.now();
    const prev = this.lastSfx.get(kind) ?? 0;
    const minGap = kind === 'draw' ? 55 : 80;
    if (now - prev < minGap) return;
    this.lastSfx.set(kind, now);
    switch (kind) {
      case 'connect':
        this.beep(320, 0.1, 'triangle', 0.14);
        this.beep(480, 0.08, 'sine', 0.07);
        break;
      case 'deliver':
        this.beep(392, 0.12, 'triangle', 0.1);
        break;
      case 'reward':
        this.beep(294, 0.14, 'triangle', 0.12);
        this.beep(440, 0.18, 'sine', 0.09);
        break;
      case 'alert':
        this.beep(165, 0.22, 'triangle', 0.06);
        break;
      case 'defeat':
        this.beep(130, 0.45, 'triangle', 0.09);
        break;
      case 'draw':
        this.beep(240 + Math.random() * 30, 0.04, 'sine', 0.035);
        break;
      case 'spawnGen':
        this.beep(196, 0.12, 'triangle', 0.09);
        this.beep(294, 0.14, 'sine', 0.06);
        break;
      case 'spawnBulb':
        this.beep(349, 0.11, 'triangle', 0.08);
        this.beep(440, 0.12, 'sine', 0.05);
        break;
    }
  }
}
